import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { toBigNumber, formatFormAmount } from '~/utils/numbers'
import { useTokenPriceWithUseAPI } from '~/hooks/useTokenPrice'
import { Currency, FixedPointNumber } from '~/utils/fixedPoint'
import { useModalToggle } from '~/state/application/hooks'
import { ApplicationModal } from '~/state/application/reducer'
import { useSpotSwapPair, useSpotSwapUserConfig } from '~/state/dex/hooks'
import { useGA, useUpdaterChainId } from '~/hooks'
import Tooltip from '~/components/Tooltip'
import Skeleton from '~/components/Exchange/components/Skeleton'
import { StyledDetailWrapper } from '~/components/Exchange/styled'
import { formatUnits } from 'viem'
import styled from 'styled-components'
import { FlexContainer } from '~/components/Layout'
import { isMainnetOrSepolia } from '~/utils/chain'

interface TradeFeeRowProps {
  tradeFee: bigint
  feeFactor: number
  loading: boolean
}

const SendBySelfTradeFee: React.FC<TradeFeeRowProps> = ({
  tradeFee,
  feeFactor,
  loading,
}) => {
  const { t } = useTranslation()
  const { trackElClickCurring } = useGA()

  const chainId = useUpdaterChainId()
  const isMainnetOrSepoliaFlag = isMainnetOrSepolia(chainId)
  const [, outputToken] = useSpotSwapPair()
  const { isCrossSwap } = useSpotSwapUserConfig()
  const tokenPrice = useTokenPriceWithUseAPI({
    tokenAddress: outputToken?.address,
    chainId: outputToken?.chainId,
  })
  const formattedTradeFee = formatUnits(tradeFee, outputToken?.decimals)
  const usdValue = toBigNumber(formattedTradeFee).times(tokenPrice)

  const usdValueLiteral = useMemo(() => {
    if (!Number(usdValue)) {
      return '~'
    } else if (Number(usdValue) < 0.01) {
      return '< $0.01'
    } else {
      return `${new FixedPointNumber(usdValue).toCurrency(Currency.USD, 2)}`
    }
  }, [usdValue])

  const toggleFeeIntro = useModalToggle(ApplicationModal.FEE_INTRO, {
    stack: true,
  })

  // 交易费费率
  const defaultFeeFactor = isMainnetOrSepoliaFlag ? 30 : 15
  const defaultFeeFactorLiteral = isMainnetOrSepoliaFlag ? '0.3%' : '0.15%'
  const isDefaultTradeFeeRate = feeFactor === defaultFeeFactor
  const isStableCoinsTradeFeeRate = feeFactor === 4
  const tradeFeeRateLiteral = `${feeFactor / 100}%`

  const isZeroTradeFee = formattedTradeFee === '0'

  const valueLiteral = isZeroTradeFee
    ? `0 ${outputToken?.symbol}`
    : Number(tradeFee)
    ? `${usdValueLiteral}(${formatFormAmount(formattedTradeFee, 3)} ${
        outputToken?.symbol
      })`
    : '~'

  const TradeFeeLabel = () => {
    const Content = () => (
      <>
        <span>{'('}</span>
        <span style={{ textDecoration: 'line-through' }}>
          {defaultFeeFactorLiteral}
        </span>
        <span style={{ color: '#5F4DBC' }}>{` ${tradeFeeRateLiteral}`}</span>
        <span>{')'}</span>
      </>
    )

    if (isDefaultTradeFeeRate) {
      return <div>{`(${defaultFeeFactorLiteral})`}</div>
    } else if (isStableCoinsTradeFeeRate) {
      return (
        <Tooltip
          placement="top"
          title={t('stablecoins_trade_fee_tooltip')}
          trigger="hover"
        >
          <Content />
        </Tooltip>
      )
    } else {
      return (
        <div
          data-trackid="webtl_instant_txfee"
          data-trackdata={JSON.stringify({ is_cross_swap: isCrossSwap })}
          onClick={(e) => trackElClickCurring(e)(toggleFeeIntro)}
          style={{ cursor: 'pointer' }}
        >
          <Content />
        </div>
      )
    }
  }

  return (
    <StyledDetailWrapper>
      <StyledLabel justify="center" align="center" gap={4}>
        <span>{t('trade_fee')}</span>
        <TradeFeeLabel />
      </StyledLabel>
      {loading ? <Skeleton /> : <StyledValue>{valueLiteral}</StyledValue>}
    </StyledDetailWrapper>
  )
}

export default SendBySelfTradeFee

const StyledLabel = styled(FlexContainer)`
  .tooltip-wrap img {
    margin-left: 0;
  }
`

const StyledValue = styled.div`
  color: ${({ theme }) => theme.colors.textBlack2};
  text-align: right;
`
